import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { GitBranch, ChevronDown, ChevronRight, FileSpreadsheet, Loader2, Circle, ShieldCheck } from "lucide-react";

const FILTROS = [
  { id: "todos", label: "Todos" },
  { id: "pendientes", label: "Pendientes" },
  { id: "resueltos", label: "Resueltos" },
  { id: "cerrado_conforme", label: "Cerrados Conforme" },
];

const ESTADO_COLORES = {
  abierto: "bg-blue-100 text-blue-700",
  en_progreso: "bg-amber-100 text-amber-700",
  resuelto: "bg-emerald-100 text-emerald-700",
  completado: "bg-emerald-100 text-emerald-700",
  cerrado: "bg-slate-200 text-slate-700",
};

const fmtFecha = (f) => {
  if (!f) return "—";
  try {
    return new Date(f).toLocaleString("es-DO", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return String(f).slice(0, 16).replace("T", " ");
  }
};

export default function HallazgosTrazabilidadTab() {
  const { getAuthHeaders } = useAuth();
  const [filtro, setFiltro] = useState("todos");
  const [tickets, setTickets] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [expandido, setExpandido] = useState({});
  const [descargando, setDescargando] = useState(false);

  const cargar = useCallback(async (f) => {
    try {
      setCargando(true);
      const res = await axios.get(`${API}/hallazgos-auditoria/trazabilidad?filtro=${f}`, getAuthHeaders());
      setTickets(res.data || []);
    } catch {
      toast.error("No se pudo cargar la trazabilidad");
    } finally {
      setCargando(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    cargar(filtro);
  }, [filtro, cargar]);

  const descargarReporte = async () => {
    try {
      setDescargando(true);
      const res = await axios.get(`${API}/hallazgos-auditoria/trazabilidad/reporte?filtro=${filtro}`, {
        ...getAuthHeaders(),
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `reporte_trazabilidad_hallazgos_${filtro}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Reporte de trazabilidad descargado");
    } catch {
      toast.error("No se pudo descargar el reporte");
    } finally {
      setDescargando(false);
    }
  };

  return (
    <div className="p-4 md:p-8" data-testid="hallazgos-trazabilidad-tab">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
        <div>
          <h2 className="text-lg font-semibold text-slate-800 dark:text-white flex items-center gap-2">
            <GitBranch className="w-5 h-5 text-[#0C314C] dark:text-blue-400" />
            Trazabilidad de Hallazgos
          </h2>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Historial completo de cada hallazgo desde su apertura hasta el cierre definitivo.
          </p>
        </div>
        <Button
          size="sm"
          onClick={descargarReporte}
          disabled={descargando || cargando}
          className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white"
          data-testid="trazabilidad-reporte-descargar-btn"
        >
          {descargando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-2" />}
          Reporte de Trazabilidad
        </Button>
      </div>

      <div className="flex gap-2 mb-5 flex-wrap">
        {FILTROS.map((f) => (
          <button
            key={f.id}
            onClick={() => setFiltro(f.id)}
            className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
              filtro === f.id
                ? "bg-[#0C314C] text-white border-[#0C314C]"
                : "bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600 hover:border-slate-400"
            }`}
            data-testid={`trazabilidad-filtro-${f.id}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {cargando ? (
        <div className="flex items-center justify-center py-16 text-slate-400">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : tickets.length === 0 ? (
        <div className="text-center py-16 text-slate-400 text-sm" data-testid="trazabilidad-vacio">
          No hay hallazgos para este filtro.
        </div>
      ) : (
        <div className="space-y-3">
          {tickets.map((t) => {
            const abierto = !!expandido[t.id];
            return (
              <div
                key={t.id}
                className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden"
                data-testid={`trazabilidad-ticket-${t.numero}`}
              >
                <button
                  onClick={() => setExpandido((p) => ({ ...p, [t.id]: !p[t.id] }))}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
                  data-testid={`trazabilidad-toggle-${t.numero}`}
                >
                  {abierto ? <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />}
                  <span className="text-xs font-mono text-slate-500 shrink-0">#{t.numero}</span>
                  <span className="text-sm font-medium text-slate-800 dark:text-white truncate flex-1">{t.titulo}</span>
                  <span className="hidden md:inline text-xs text-slate-400 shrink-0">{t.categoria_name || "—"}</span>
                  <Badge className={`${ESTADO_COLORES[t.estado] || "bg-slate-100 text-slate-600"} border-0 text-[11px] shrink-0`}>
                    {(t.estado || "").replace("_", " ")}
                  </Badge>
                  {t.estatus_final === "Cerrado Conforme" && (
                    <Badge className="bg-emerald-600 text-white border-0 text-[11px] shrink-0 flex items-center gap-1">
                      <ShieldCheck className="w-3 h-3" /> Cerrado Conforme
                    </Badge>
                  )}
                  <span className="hidden lg:inline text-[11px] text-slate-400 shrink-0">
                    {(t.historial_estados || []).length} evento{(t.historial_estados || []).length === 1 ? "" : "s"}
                  </span>
                </button>
                {abierto && (
                  <div className="px-5 pb-4 pt-1 border-t border-slate-100 dark:border-slate-700" data-testid={`trazabilidad-detalle-${t.numero}`}>
                    <div className="relative ml-2 mt-3 space-y-4 before:absolute before:left-[5px] before:top-1 before:bottom-1 before:w-px before:bg-slate-200 dark:before:bg-slate-600">
                      {(t.historial_estados || []).map((h) => (
                        <div key={h.id} className="relative pl-6">
                          <Circle className="w-[11px] h-[11px] absolute left-0 top-1 fill-[#0C314C] text-[#0C314C] dark:fill-blue-400 dark:text-blue-400" />
                          <div className="text-xs text-slate-400">{fmtFecha(h.fecha)}</div>
                          <div className="text-sm text-slate-700 dark:text-slate-200">
                            {h.estado_anterior ? (
                              <span>
                                <span className="capitalize">{(h.estado_anterior || "").replace("_", " ")}</span>
                                {" → "}
                                <span className="font-semibold capitalize">{(h.estado || "").replace("_", " ")}</span>
                              </span>
                            ) : (
                              <span className="font-semibold capitalize">{(h.estado || "").replace("_", " ")}</span>
                            )}
                            {h.usuario_nombre && <span className="text-slate-400"> · {h.usuario_nombre}</span>}
                          </div>
                          {h.detalle && <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{h.detalle}</div>}
                        </div>
                      ))}
                      {(t.historial_estados || []).length === 0 && (
                        <div className="relative pl-6 text-xs text-slate-400">Sin historial registrado (hallazgo anterior a la trazabilidad).</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
